export const TWO_FACTOR_CHALLENGE_STORAGE_KEY = 'twoFactorChallenge';

// Preview hooks for the setup dialog. Login itself always calls the real API.
export const TWO_FACTOR_UI_PREVIEW = false;

export type TwoFactorChallenge = {
    challengeToken: string;
    userName?: string | null;
};

export type TwoFactorSetup = {
    otpauthUrl: string;
    manualKey: string;
};

const CHALLENGE_TOKEN_KEYS = ['challengeToken', 'twoFactorToken', 'tempToken', 'twoFactorChallengeToken'];
const SETUP_URL_KEYS = ['otpauthUrl', 'otpAuthUri', 'otpauthUri', 'qrText', 'authenticatorUri'];
const SETUP_SECRET_KEYS = ['manualKey', 'secret', 'manualEntryKey', 'base32Secret'];

function payloadOf(body: any): any {
    if (body?.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
        return body.data;
    }
    return body && typeof body === 'object' ? body : {};
}

function firstString(source: any, keys: string[]): string {
    if (!source || typeof source !== 'object') {
        return '';
    }
    for (const key of keys) {
        const value = source[key];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }
    return '';
}

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

function bytesToBase32(bytes: Uint8Array): string {
    let bits = 0;
    let value = 0;
    let output = '';
    for (let index = 0; index < bytes.length; index += 1) {
        const byte = bytes[index];
        value = (value << 8) | byte;
        bits += 8;
        while (bits >= 5) {
            output += BASE32_ALPHABET[(value >>> (bits - 5)) & 31];
            bits -= 5;
        }
    }
    if (bits > 0) {
        output += BASE32_ALPHABET[(value << (5 - bits)) & 31];
    }
    return output;
}

export function toAuthenticatorKey(secret: string): string {
    const compact = secret.trim().replace(/[\s-]+/g, '').replace(/=+$/g, '').toUpperCase();
    if (/^[A-Z2-7]+$/.test(compact)) {
        return compact;
    }
    const base64 = secret.trim().replace(/\s/g, '');
    if (/^[A-Za-z0-9+/]+={0,2}$/.test(base64)) {
        try {
            const binary = atob(base64);
            const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
            const encoded = bytesToBase32(bytes);
            if (/^[A-Z2-7]+$/.test(encoded)) {
                return encoded;
            }
        } catch {
            // The secret is not base64. Fall through and keep only base32 characters.
        }
    }
    return compact.replace(/[^A-Z2-7]/g, '');
}

export function readOtpauthSetup(value: string): {otpauthUrl: string, manualKey: string} | null {
    const sourceUrl = value.trim();
    if (!/^otpauth:\/\//i.test(sourceUrl)) {
        return null;
    }
    const manualKey = toAuthenticatorKey(secretFromOtpauth(sourceUrl));
    if (!manualKey) {
        return null;
    }
    const otpauthUrl = sourceUrl.replace(/([?&]secret=)[^&]*/i, `$1${manualKey}`);
    return {otpauthUrl, manualKey};
}

function secretFromOtpauth(url: string): string {
    const match = url.match(/[?&]secret=([^&]+)/i);
    if (!match) {
        return '';
    }
    try {
        return decodeURIComponent(match[1]);
    } catch {
        return match[1];
    }
}

export function buildOtpauthUrl(secret: string, account?: string | null, issuer = 'CallCenter'): string {
    const label = account ? `${issuer}:${account}` : issuer;
    const params = new URLSearchParams({
        secret,
        issuer,
        digits: '6',
        period: '30'
    });
    return `otpauth://totp/${encodeURIComponent(label)}?${params.toString()}`;
}

const PNG_BASE64_PREFIX = 'iVBORw0KGgo';

export function readLoginAuthenticatorQr(body: any): string | null {
    if (body?.statusCode !== 200 || typeof body?.data !== 'string') {
        return null;
    }
    const trimmed = body.data.trim();
    if (trimmed.startsWith('data:image/')) {
        return trimmed;
    }
    const payload = trimmed.replace(/\s/g, '');
    if (!payload.startsWith(PNG_BASE64_PREFIX) || payload.length < 100) {
        return null;
    }
    return payload;
}

export function readLoginAuthCodeRequired(body: any): boolean {
    const data = body?.data;
    return Boolean(data && typeof data === 'object' && !Array.isArray(data) && data.isAuth === true);
}

function normalizeNoticeMessage(message: string): string | null {
    const normalized = message
        .replace(/\\r\\n|\\n|\\r/g, '\n')
        .replace(/\r\n|\r/g, '\n')
        .replace(/[ \t]*\n[ \t]*/g, '\n')
        .replace(/\n{2,}/g, '\n')
        .trim();
    return normalized || null;
}

export function readLoginAuthNotice(body: any): string | null {
    if (body?.statusCode !== 199 || typeof body?.message !== 'string') {
        return null;
    }
    return normalizeNoticeMessage(body.message);
}

export function isFinishedLogin(body: any): boolean {
    const statusCode = body?.statusCode;
    return (statusCode === 200 || statusCode === 199) && Boolean(body?.data?.jwtToken?.jwtToken);
}

export function readLoginSuccessNotice(body: any): string | null {
    if (!isFinishedLogin(body) || body?.statusCode !== 199) {
        return null;
    }
    return readLoginAuthNotice(body);
}

export function loginQrImageSrc(qr: string): string {
    if (qr.startsWith('data:image/')) {
        return qr;
    }
    return `data:image/png;base64,${qr}`;
}

export function readTwoFactorChallenge(body: any): TwoFactorChallenge | null {
    const data = payloadOf(body);
    const challengeToken = firstString(data, CHALLENGE_TOKEN_KEYS);
    if (!challengeToken) {
        return null;
    }
    const flag = data.requiresTwoFactor ?? data.twoFactorRequired ?? data.needTwoFactor ?? data.isTwoFactorRequired;
    if (flag === false) {
        return null;
    }
    const hasJwt = Boolean(data?.jwtToken?.jwtToken);
    if (flag !== true && hasJwt) {
        return null;
    }
    return {
        challengeToken,
        userName: firstString(data, ['userName', 'username']) || null
    };
}

export function readTwoFactorSetup(body: any): TwoFactorSetup | null {
    const data = payloadOf(body);
    const otpauthUrl = firstString(data, SETUP_URL_KEYS);
    let manualKey = firstString(data, SETUP_SECRET_KEYS) || secretFromOtpauth(otpauthUrl);
    const account = firstString(data, ['account', 'userName', 'username', 'email']);
    if (!otpauthUrl && !manualKey) {
        return null;
    }
    return {
        otpauthUrl: otpauthUrl || buildOtpauthUrl(manualKey, account),
        manualKey
    };
}

export function readTwoFactorEnabled(body: any): boolean | null {
    if (typeof body?.data === 'boolean') {
        return body.data;
    }
    const data = payloadOf(body);
    const value = data.enabled ?? data.twoFactorEnabled ?? data.isEnabled ?? data.isTwoFactorEnabled;
    return typeof value === 'boolean' ? value : null;
}

export function readBackupCodes(body: any): string[] {
    const data = payloadOf(body);
    const list = data.backupCodes || data.recoveryCodes || body?.backupCodes;
    if (!Array.isArray(list)) {
        return [];
    }
    return list.map((item) => String(item || '').trim()).filter(Boolean);
}

export function readApiMessage(body: any, fallback: string): string {
    const message = body?.message || body?.title || body?.data?.message || body?.data?.title;
    return typeof message === 'string' && message.trim() ? message.trim() : fallback;
}

export function storeTwoFactorChallenge(challenge: TwoFactorChallenge) {
    sessionStorage.setItem(TWO_FACTOR_CHALLENGE_STORAGE_KEY, JSON.stringify(challenge));
    sessionStorage.removeItem('isLoggedOut');
}

export function loadTwoFactorChallenge(): TwoFactorChallenge | null {
    try {
        const raw = sessionStorage.getItem(TWO_FACTOR_CHALLENGE_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (!parsed?.challengeToken || typeof parsed.challengeToken !== 'string') {
            return null;
        }
        return {
            challengeToken: parsed.challengeToken,
            userName: parsed.userName || null
        };
    } catch {
        return null;
    }
}

export function removeTwoFactorChallenge() {
    sessionStorage.removeItem(TWO_FACTOR_CHALLENGE_STORAGE_KEY);
}
